<template>
  <section class="checkout-payment-embed checkout-payment-embed--stripe">
    <div class="checkout-payment-embed__hero">
      <div>
        <span class="checkout-payment-embed__label">Pago con tarjeta</span>
        <h3 class="checkout-payment-embed__title">Ingresa los datos de tu tarjeta para cerrar esta orden.</h3>
        <p class="checkout-payment-embed__copy">Si tu banco pide validación adicional, la hacemos aquí mismo sin sacarte del checkout.</p>
      </div>

      <div class="checkout-payment-embed__chips">
        <span>Débito y crédito</span>
        <span>Confirmación integrada</span>
        <span>Validación 3DS</span>
      </div>
    </div>

    <div v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</div>
    <div v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</div>

    <div v-show="loading" class="checkout-payment-embed__loading">
      <span class="checkout-payment-embed__loading-ring"></span>
      <div>
        <strong>Preparando formulario seguro...</strong>
        <small>Estamos montando la tarjeta dentro de FOMO.</small>
      </div>
    </div>

    <div class="checkout-payment-embed__mount">
      <div class="checkout-card-field checkout-card-field--full">
        <span class="checkout-card-field__label">Número de tarjeta</span>
        <div class="checkout-card-field__control">
          <div ref="cardNumberElement" class="checkout-card-field__mount"></div>
        </div>
      </div>

      <div class="checkout-card-grid">
        <div class="checkout-card-field">
          <span class="checkout-card-field__label">Vencimiento</span>
          <div class="checkout-card-field__control">
            <div ref="cardExpiryElement" class="checkout-card-field__mount"></div>
          </div>
        </div>

        <div class="checkout-card-field">
          <span class="checkout-card-field__label">Código de seguridad</span>
          <div class="checkout-card-field__control">
            <div ref="cardCvcElement" class="checkout-card-field__mount"></div>
          </div>
        </div>
      </div>

      <div class="checkout-card-field checkout-card-field--full">
        <span class="checkout-card-field__label">País</span>
        <div class="checkout-card-field__control checkout-card-field__control--select">
          <select v-model="selectedCountryCode" class="checkout-card-field__select" :disabled="countriesLoading || confirming">
            <option value="">Selecciona un país</option>
            <option v-for="country in countries" :key="country.code" :value="country.code">{{ country.name }}</option>
          </select>
        </div>
      </div>
    </div>

    <p class="checkout-payment-embed__hint">Total a confirmar: <strong>{{ formattedAmount }}</strong></p>

    <div class="workspace-actions workspace-actions--end checkout-payment-embed__actions">
      <button class="btn btn-primary liquid-page-button checkout-payment-embed__button" type="button" :disabled="!canSubmit" @click="confirmPayment">
        <span class="liquid-page-button__glow"></span>
        <span class="liquid-page-button__ring"></span>
        <span class="liquid-page-button__inner">
          <span class="liquid-page-button__copy">{{ confirming ? 'Confirmando...' : `Pagar ${formattedAmount}` }}</span>
          <span class="liquid-page-button__icon"><i class="fas fa-lock"></i></span>
        </span>
      </button>
    </div>
  </section>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import { apiService } from '../../common/api.services.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'

const FALLBACK_COUNTRIES = [
  { code: 'AR', name: 'Argentina' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BR', name: 'Brasil' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'MX', name: 'México' },
  { code: 'PE', name: 'Perú' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'UY', name: 'Uruguay' },
]

export default {
  name: 'StripePaymentElement',
  props: {
    attemptToken: {
      type: String,
      required: true,
    },
    clientSecret: {
      type: String,
      required: true,
    },
    publishableKey: {
      type: String,
      required: true,
    },
    customerName: {
      type: String,
      default: '',
    },
    customerEmail: {
      type: String,
      default: '',
    },
    customerPhone: {
      type: String,
      default: '',
    },
    defaultCountryCode: {
      type: String,
      default: '',
    },
    amount: {
      type: [Number, String],
      default: 0,
    },
    currency: {
      type: String,
      default: 'USD',
    },
  },
  data() {
    return {
      loading: false,
      confirming: false,
      ready: false,
      stripe: null,
      elements: null,
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,
      countries: [],
      countriesLoading: false,
      selectedCountryCode: '',
      fieldState: {
        number: false,
        expiry: false,
        cvc: false,
      },
      errorMessage: '',
      successMessage: '',
    }
  },
  computed: {
    formattedAmount() {
      return formatCurrencyAmount(this.amount, normalizeCurrencyCode(this.currency, 'USD'))
    },
    billingDetails() {
      const details = {}
      const name = typeof this.customerName === 'string' ? this.customerName.trim() : ''
      const email = typeof this.customerEmail === 'string' ? this.customerEmail.trim() : ''
      const phone = typeof this.customerPhone === 'string' ? this.customerPhone.trim() : ''
      const country = typeof this.selectedCountryCode === 'string' ? this.selectedCountryCode.trim().toUpperCase() : ''

      if (name) {
        details.name = name
      }
      if (email) {
        details.email = email
      }
      if (phone) {
        details.phone = phone
      }
      if (country) {
        details.address = {
          country,
        }
      }

      return details
    },
    canSubmit() {
      return !this.loading && !this.confirming && this.ready && this.fieldState.number && this.fieldState.expiry && this.fieldState.cvc && !!this.selectedCountryCode
    },
  },
  mounted() {
    this.initialize()
  },
  beforeUnmount() {
    this.unmountElement('cardNumberElement')
    this.unmountElement('cardExpiryElement')
    this.unmountElement('cardCvcElement')
    this.elements = null
    this.stripe = null
  },
  methods: {
    normalizeCountryCode(value = '') {
      return typeof value === 'string' ? value.trim().toUpperCase() : ''
    },
    normalizeCountries(items = []) {
      const normalized = Array.isArray(items)
        ? items
            .filter((item) => item && item.code)
            .map((item) => ({
              code: this.normalizeCountryCode(item.code),
              name: item.name || item.code,
            }))
        : []

      return normalized.sort((left, right) => left.name.localeCompare(right.name, 'es', { sensitivity: 'base' }))
    },
    async loadCountries() {
      this.countriesLoading = true

      try {
        const response = await apiService('/api/countries/')
        const normalized = this.normalizeCountries(response)
        this.countries = normalized.length ? normalized : FALLBACK_COUNTRIES
      } catch (_error) {
        this.countries = FALLBACK_COUNTRIES
      } finally {
        this.countriesLoading = false
      }

      const defaultCode = this.normalizeCountryCode(this.defaultCountryCode)
      if (defaultCode && this.countries.some((country) => country.code === defaultCode)) {
        this.selectedCountryCode = defaultCode
      }
    },
    unmountElement(key) {
      if (this[key]) {
        this[key].unmount()
        this[key].destroy()
        this[key] = null
      }
    },
    handleElementChange(field, event) {
      this.fieldState[field] = !!event.complete

      if (event.error && event.error.message) {
        this.errorMessage = event.error.message
        return
      }

      this.errorMessage = ''
    },
    async initialize() {
      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''

      await this.loadCountries()

      if (!this.publishableKey || !this.clientSecret) {
        this.loading = false
        this.errorMessage = 'Faltan credenciales públicas de Stripe o client secret.'
        return
      }

      this.stripe = await loadStripe(this.publishableKey)
      if (!this.stripe) {
        this.loading = false
        this.errorMessage = 'Stripe no pudo cargarse en este navegador.'
        return
      }

      const compactViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches

      this.elements = this.stripe.elements({ locale: 'es' })

      const baseStyle = {
        color: '#edf2ff',
        fontFamily: 'Space Grotesk, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: compactViewport ? '16px' : '17px',
        '::placeholder': {
          color: '#96a0ba',
        },
        iconColor: '#d4dced',
      }

      const invalidStyle = {
        color: '#ff7b9c',
        iconColor: '#ff7b9c',
      }

      const sharedOptions = {
        style: {
          base: baseStyle,
          invalid: invalidStyle,
        },
      }

      this.cardNumberElement = this.elements.create('cardNumber', {
        ...sharedOptions,
        showIcon: true,
        placeholder: '1234 1234 1234 1234',
      })
      this.cardExpiryElement = this.elements.create('cardExpiry', {
        ...sharedOptions,
        placeholder: 'MM / AA',
      })
      this.cardCvcElement = this.elements.create('cardCvc', {
        ...sharedOptions,
        placeholder: 'CVC',
      })

      this.cardNumberElement.on('change', (event) => this.handleElementChange('number', event))
      this.cardExpiryElement.on('change', (event) => this.handleElementChange('expiry', event))
      this.cardCvcElement.on('change', (event) => this.handleElementChange('cvc', event))

      this.cardNumberElement.mount(this.$refs.cardNumberElement)
      this.cardExpiryElement.mount(this.$refs.cardExpiryElement)
      this.cardCvcElement.mount(this.$refs.cardCvcElement)

      this.ready = true
      this.loading = false
    },
    async confirmPayment() {
      if (!this.stripe || !this.cardNumberElement || !this.canSubmit) {
        return
      }

      this.confirming = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.clientSecret, {
          payment_method: {
            card: this.cardNumberElement,
            billing_details: this.billingDetails,
          },
        })

        if (error) {
          this.errorMessage = error.message || 'Stripe no pudo confirmar el pago.'
          return
        }

        this.successMessage = 'Pago confirmado. Estamos validando tu compra.'
        this.$emit('completed', {
          attemptToken: this.attemptToken,
          paymentIntentId: paymentIntent && paymentIntent.id ? paymentIntent.id : '',
          status: paymentIntent && paymentIntent.status ? paymentIntent.status : 'processing',
        })
      } catch (_error) {
        this.errorMessage = (_error && _error.message) || 'Stripe tardó demasiado o no pudo completar la confirmación. Intenta de nuevo.'
      } finally {
        this.confirming = false
      }
    },
  },
}
</script>

<style scoped>
.checkout-payment-embed--stripe {
  display: grid;
  gap: 14px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.checkout-payment-embed__hero {
  display: grid;
  gap: 10px;
}

.checkout-payment-embed__title {
  margin: 6px 0 8px;
  color: #f7f9fc;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
}

.checkout-payment-embed__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checkout-payment-embed__chips span {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(198, 205, 220, 0.12);
  color: #dde5f3;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.checkout-payment-embed__copy,
.checkout-payment-embed__loading {
  margin: 0;
  color: rgba(207, 215, 230, 0.82);
}

.checkout-payment-embed__loading {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(198, 205, 220, 0.09);
}

.checkout-payment-embed__loading strong,
.checkout-payment-embed__hint {
  color: #eef2f7;
}

.checkout-payment-embed__loading small {
  display: block;
  color: rgba(186, 194, 208, 0.72);
}

.checkout-payment-embed__loading-ring {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.22);
  border-top-color: #f8fafc;
  animation: stripe-spin 0.9s linear infinite;
}

.checkout-payment-embed__mount {
  display: grid;
  gap: 14px;
  width: 100%;
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.01)),
    rgba(4, 6, 12, 0.38);
  border: 1px solid rgba(198, 205, 220, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  box-sizing: border-box;
}

.checkout-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.checkout-card-field {
  display: grid;
  gap: 8px;
}

.checkout-card-field--full {
  grid-column: 1 / -1;
}

.checkout-card-field__label {
  color: #dbe4f4;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.checkout-card-field__control {
  display: flex;
  align-items: center;
  min-height: 58px;
  padding: 0 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(198, 205, 220, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  box-sizing: border-box;
}

.checkout-card-field__control--select {
  position: relative;
  padding-right: 40px;
}

.checkout-card-field__control--select::after {
  content: '▾';
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(207, 216, 234, 0.86);
  font-size: 1rem;
  pointer-events: none;
}

.checkout-card-field__mount {
  width: 100%;
}

.checkout-card-field__select {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: #edf2ff;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  appearance: none;
}

.checkout-card-field__select:disabled {
  opacity: 0.7;
  cursor: wait;
}

.checkout-card-field__select option {
  color: #0d1017;
}

.checkout-payment-embed__hint {
  margin: -2px 0 0;
  font-size: 0.92rem;
  line-height: 1.5;
}

.checkout-payment-embed__actions {
  width: 100%;
  padding-top: 0;
}

.checkout-payment-embed__button {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 68px;
  border: 0;
}

.checkout-payment-embed__button .liquid-page-button__inner {
  justify-content: space-between;
  padding: 0 24px;
}

@keyframes stripe-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .checkout-payment-embed--stripe {
    gap: 10px;
  }

  .checkout-payment-embed__hero {
    gap: 8px;
  }

  .checkout-payment-embed__title {
    margin: 4px 0 6px;
    font-size: 1.04rem;
    line-height: 1.2;
  }

  .checkout-payment-embed__copy {
    font-size: 0.86rem;
    line-height: 1.45;
  }

  .checkout-payment-embed__chips {
    gap: 8px;
  }

  .checkout-payment-embed__chips span {
    min-height: 32px;
    padding: 0 10px;
    font-size: 0.68rem;
    letter-spacing: 0.05em;
  }

  .checkout-payment-embed__loading {
    gap: 10px;
    padding: 8px 10px;
  }

  .checkout-payment-embed__mount {
    gap: 10px;
    padding: 14px 12px 12px;
    border-radius: 18px;
  }

  .checkout-card-grid {
    gap: 10px;
  }

  .checkout-card-field {
    gap: 6px;
  }

  .checkout-card-field__label {
    font-size: 0.76rem;
    letter-spacing: 0.05em;
  }

  .checkout-card-field__control {
    min-height: 54px;
    padding: 0 14px;
    border-radius: 16px;
  }

  .checkout-card-field__control--select {
    padding-right: 36px;
  }

  .checkout-card-field__control--select::after {
    right: 14px;
  }

  .checkout-card-field__select {
    font-size: 0.98rem;
  }

  .checkout-payment-embed__hint {
    font-size: 0.86rem;
  }

  .checkout-payment-embed__button {
    min-height: 62px;
  }

  .checkout-payment-embed__button .liquid-page-button__inner {
    padding: 0 18px;
  }
}
</style>
